import { useCallback } from "react";
import { toast } from "sonner";
import { usePermissions } from "@/hooks/usePermissions";
import { usePermissionDenied } from "@/hooks/usePermissionDenied";
import { useLanguage } from "@/lib/i18n/LanguageContext";

/**
 * Why a new issue cannot be filed in `projectId`, or null when it can. The form
 * offers no project choice — the issue goes to the project its entry point is
 * in, or nowhere — so the answer is about that one project and never another.
 */
export function newIssueRefusal(
  projectId: number | null | undefined,
  canFileInto: (projectId: number) => boolean
): "noProject" | "denied" | null {
  if (projectId == null) return "noProject";
  return canFileInto(projectId) ? null : "denied";
}

/**
 * Run a New issue entry point for the project the issue would be filed in, or
 * refuse it before any form opens: a member without MANAGE_PM there gets the
 * permission toast (the pm_issues insert policy would refuse the save), and an
 * entry point with no project in scope says so. Never falls back to another
 * project the member could file into — that would file the issue somewhere
 * other than the page they are on.
 */
export function useNewIssueGuard() {
  const { canManagePMOnProject } = usePermissions();
  const denyPermission = usePermissionDenied();
  const { t } = useLanguage();
  const noProject = t.pm.issues.draft.noProject;
  return useCallback(
    (projectId: number | null | undefined, run: () => void): void => {
      const refusal = newIssueRefusal(projectId, canManagePMOnProject);
      if (refusal === null) {
        run();
      } else if (refusal === "denied" && projectId != null) {
        denyPermission("manageIssues", projectId);
      } else {
        toast.warning(noProject, { id: "new-issue:no-project" });
      }
    },
    [canManagePMOnProject, denyPermission, noProject]
  );
}
